import { useMemo } from 'react';

import { Text } from '@rbilabs/universal-components';
import { MenuAcceleratorCrownSvg } from 'features/menu/components/menu-accelerator-badge/menu-accelerator-badge-svg';
import { BonusType, BonusTypes } from 'features/menu/components/menu-accelerator-badge/types';
import { parseBonusPoints } from 'features/menu/components/menu-accelerator-badge/utils';
import { useAcceleratorBadgesOnMenu } from 'features/menu/hooks/use-accelerator-badges-on-menu';
import { StyleSheet, View } from 'react-native';

import { tRich } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

interface IMenuAcceleratorPromotionBanner {
  entries: string[];
  isFromCart?: boolean;
}
export const MenuAcceleratorPromotionBanner = ({
  entries,
  isFromCart = false,
}: IMenuAcceleratorPromotionBanner) => {
  const {
    cartRequirement,
    experimentEnabled,
    bonusPoints,
    pointsMultiplier,
    shouldShowFullMenuBadge,
    promotionId,
  } = useAcceleratorBadgesOnMenu({
    skipExposureLogging: true,
    menuData: [],
  });

  const shouldDisplayPromotionBanner = useMemo(() => {
    if (!experimentEnabled || !entries.length || !promotionId) {
      return false;
    }

    const shouldShowForMatchingEntries = entries.some(entryId => cartRequirement.has(entryId));

    return shouldShowForMatchingEntries || shouldShowFullMenuBadge;
  }, [cartRequirement, entries, experimentEnabled, promotionId, shouldShowFullMenuBadge]);

  if (!shouldDisplayPromotionBanner) {
    return null;
  }

  const bonusType: BonusType = bonusPoints ? BonusTypes.BONUS_POINTS : BonusTypes.POINTS_MULTIPLIER;
  const bonusPointsText = `+${parseBonusPoints(bonusPoints)}`;
  const pointMultiplierText = `${pointsMultiplier}x`;
  const pointsText = bonusType === BonusTypes.BONUS_POINTS ? bonusPointsText : pointMultiplierText;

  const formattedPoints = <Text fontWeight="bold">{pointsText}</Text>;

  const reviewOrderMessage = tRich('menuAcceleratorReviewOrderMessage', {
    points: formattedPoints,
  });

  const cartMessage = tRich('menuAcceleratorCartMessage', { points: formattedPoints });

  return (
    <View style={styles.container}>
      <View style={styles.icon}>
        <MenuAcceleratorCrownSvg />
      </View>
      <View style={styles.message}>
        <Text variant="copyOne">{isFromCart ? cartMessage : reviewOrderMessage}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    width: '100%',
    borderRadius: 10,
    padding: 8,
    backgroundColor: primitive.$meltyYellowOpacity20,
    alignItems: 'flex-start',
    gap: spacing.$1,
  },
  icon: { marginTop: spacing[spacing.$0_5], width: 16 },
  message: {
    flex: 1,
  },
});
